import React, { useEffect, useRef, useState } from 'react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const GEO_DENIED_KEY = 'portal:geoDenied';

/** Reverse-geocode lat/lng via Mapbox to a human-readable place name.
 *  Used by both the auto-detect flow and any caller that has raw coords
 *  (e.g. the browser's geolocation API). */
export async function reverseGeocodeCoords(lat, lng) {
  if (!MAPBOX_TOKEN) return null;
  try {
    const r = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`
      + `?types=place,locality,neighborhood&limit=1&language=en&access_token=${MAPBOX_TOKEN}`,
    );
    const data = await r.json();
    const f = (data.features || [])[0];
    if (!f) return { lat, lng, label: `${lat.toFixed(3)}, ${lng.toFixed(3)}` };
    return { lat, lng, label: f.place_name || f.text || '' };
  } catch {
    return { lat, lng, label: `${lat.toFixed(3)}, ${lng.toFixed(3)}` };
  }
}

/** Prompt the browser for the user's current location. Returns
 *  {lat, lng, label} on success, null on denial / failure. Records denial
 *  in sessionStorage so re-prompts within the session don't spam the user. */
export async function detectMyLocation() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  try {
    const denied = sessionStorage.getItem(GEO_DENIED_KEY) === '1';
    if (denied) return null;
  } catch {}
  const coords = await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {
        try { sessionStorage.setItem(GEO_DENIED_KEY, '1'); } catch {}
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  });
  if (!coords) return null;
  return reverseGeocodeCoords(coords.lat, coords.lng);
}

/**
 * PortalLocationPicker — single-location Mapbox picker for "Where are you right now".
 * Drives the 50mi proximity filter on the Signal wall. Different from
 * PortalCityPicker (multi-select, slug-centric) because the wall filter
 * needs exactly one (lat, lng, label) anchor.
 */
export default function PortalLocationPicker({ current, onChange, radiusMiles = 50 }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [editing, setEditing] = useState(!current?.label);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const useMyLocation = async () => {
    setDetecting(true);
    const detected = await detectMyLocation();
    if (detected) {
      await onChange(detected);
      setEditing(false);
    }
    setDetecting(false);
  };

  useEffect(() => { if (current?.label) setEditing(false); }, [current?.label]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q || !MAPBOX_TOKEN) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`
          + `?types=place,locality,neighborhood&limit=6&language=en&access_token=${MAPBOX_TOKEN}`;
        const r = await fetch(url);
        const data = await r.json();
        const items = (data.features || []).map((f) => {
          const text = f.text || '';
          const ctx = f.context || [];
          const region = ctx.find((c) => (c.id || '').startsWith('region.'))?.text;
          const country = ctx.find((c) => (c.id || '').startsWith('country.'))?.text;
          const label = [text, region, country].filter(Boolean).join(', ');
          const [lng, lat] = f.center || [];
          return { label, lat, lng };
        }).filter((r) => typeof r.lat === 'number');
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const pick = async (item) => {
    setSaving(true);
    await onChange(item);
    setSaving(false);
    setQuery('');
    setResults([]);
    setOpen(false);
    setEditing(false);
  };

  if (!editing && current?.label) {
    return (
      <div className="portal-card" style={{ padding: 16, marginBottom: 18 }}>
        <p className="portal-label" style={{ marginBottom: 6 }}>WHERE YOU ARE</p>
        <p className="portal-serif" style={{ fontSize: 18, marginBottom: 4 }}>{current.label}</p>
        <p className="portal-whisper" style={{ marginBottom: 10 }}>
          Showing Signals within <strong>{radiusMiles}mi</strong>.
        </p>
        <button type="button" onClick={() => { setEditing(true); setTimeout(() => inputRef.current?.focus(), 0); }} className="portal-btn ghost" style={{ padding: '8px 14px', fontSize: 10 }}>
          CHANGE
        </button>
      </div>
    );
  }

  return (
    <div className="portal-card" style={{ padding: 16, marginBottom: 18 }}>
      <p className="portal-label" style={{ marginBottom: 6 }}>WHERE ARE YOU?</p>
      <p className="portal-whisper" style={{ marginBottom: 10 }}>
        We'll show you Signals within {radiusMiles} miles.
      </p>
      <button
        type="button"
        onClick={useMyLocation}
        disabled={detecting || saving}
        className="portal-btn ghost"
        style={{ padding: '8px 14px', fontSize: 10, marginBottom: 10 }}
      >
        {detecting ? 'DETECTING…' : 'USE MY LOCATION'}
      </button>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          className="portal-input"
          placeholder="Search a city or neighborhood…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          disabled={saving}
        />
        {open && (results.length > 0 || searching || query.trim()) && (
          <div className="portal-card" style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            padding: 0, maxHeight: 260, overflowY: 'auto', zIndex: 5,
          }}>
            {searching && results.length === 0 ? (
              <p className="portal-mono" style={{ padding: 12, fontSize: 11, color: 'rgba(236,231,218,0.34)' }}>SEARCHING…</p>
            ) : results.length === 0 ? (
              <p className="portal-mono" style={{ padding: 12, fontSize: 11, color: 'rgba(236,231,218,0.34)' }}>NO RESULTS</p>
            ) : (
              results.map((r) => (
                <button
                  key={`${r.label}-${r.lat}-${r.lng}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(r)}
                  className="portal-mono"
                  style={{
                    display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left',
                    background: 'transparent', border: 'none', borderBottom: '1px solid rgba(236,231,218,0.12)',
                    color: '#ECE7DA', fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em',
                  }}
                >
                  {r.label.toUpperCase()}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {current?.label && (
        <button type="button" onClick={() => setEditing(false)} className="portal-mono" style={{ background: 'none', border: 'none', color: 'rgba(236,231,218,0.34)', fontSize: 10, marginTop: 8, cursor: 'pointer' }}>
          ← KEEP {current.label.toUpperCase()}
        </button>
      )}
    </div>
  );
}
